/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { defaultGetItemKey } from '../useCommandIntelligenceSession.js';
import { getCommandIntelligenceAttachModalStyles } from './CommandIntelligenceAttachModal.styles.js';
import { filterContextItemsBySearch } from './utils/filterContextItemsBySearch.js';
import { resolveContextItemRowLabels, resolveContextItemSearchText } from './utils/resolveContextItemLabels.js';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

export const COMMAND_INTELLIGENCE_ATTACH_MODAL_MAX_ADD = 25;

const ATTACH_MODAL_MESSAGE_IDS = {
  title: 'command_intelligence_attach_modal_title',
  subtitle: 'command_intelligence_attach_modal_subtitle',
  searchPlaceholder: 'command_intelligence_attach_modal_search_placeholder',
  empty: 'command_intelligence_attach_modal_empty',
  noResults: 'command_intelligence_attach_modal_no_results',
  poolCaption: 'command_intelligence_attach_modal_pool',
  attachedBadge: 'command_intelligence_attach_modal_attached_badge',
  confirm: 'command_intelligence_attach_modal_attach',
  confirmCount: 'command_intelligence_attach_modal_attach_count',
  closeAria: 'command_intelligence_attach_modal_close_aria',
};

const CommandIntelligenceAttachModal = ({
  open,
  onClose,
  availableItems,
  attachedItems,
  onApply,
  disabled,
  getItemKey = defaultGetItemKey,
  getSearchText,
  maxAddSelection = COMMAND_INTELLIGENCE_ATTACH_MODAL_MAX_ADD,
  testIdPrefix = 'attach-context',
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = getCommandIntelligenceAttachModalStyles(theme);
  const [searchQuery, setSearchQuery] = useState('');
  const [addOnlySelectedKeys, setAddOnlySelectedKeys] = useState(() => new Set());

  const resolveSearchText = useCallback(
    item => (typeof getSearchText === 'function' ? getSearchText(item) : resolveContextItemSearchText(item)),
    [getSearchText]
  );

  const attachedKeySet = useMemo(
    () => new Set((attachedItems ?? []).map(item => getItemKey(item)).filter(Boolean)),
    [attachedItems, getItemKey]
  );

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setAddOnlySelectedKeys(new Set());
    }
  }, [open]);

  const filteredItems = useMemo(
    () => filterContextItemsBySearch({ items: availableItems, query: searchQuery, getSearchText: resolveSearchText }),
    [availableItems, resolveSearchText, searchQuery]
  );

  const pendingAddCount = addOnlySelectedKeys.size;
  const hasNoItems = (availableItems?.length ?? 0) === 0;
  const hasNoSearchResults = Boolean(searchQuery.trim()) && !hasNoItems && filteredItems.length === 0;

  const resetAndClose = useCallback(() => {
    setSearchQuery('');
    setAddOnlySelectedKeys(new Set());
    onClose?.();
  }, [onClose]);

  const handleSearchChange = useCallback(event => {
    setSearchQuery(event.target?.value ?? '');
  }, []);

  const toggleAddOnlyItem = useCallback(
    key => {
      if (!key || attachedKeySet.has(key) || disabled) {
        return;
      }

      setAddOnlySelectedKeys(previous => {
        const next = new Set(previous);

        if (next.has(key)) {
          next.delete(key);
        } else if (next.size < maxAddSelection) {
          next.add(key);
        }

        return next;
      });
    },
    [attachedKeySet, disabled, maxAddSelection]
  );

  const handleConfirm = useCallback(() => {
    const picked = (availableItems ?? []).filter(item => addOnlySelectedKeys.has(getItemKey(item)));

    if (picked.length) {
      onApply?.(picked);
    }

    resetAndClose();
  }, [addOnlySelectedKeys, availableItems, getItemKey, onApply, resetAndClose]);

  const poolCaption = useMemo(() => {
    if (hasNoItems) {
      return intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.empty });
    }

    return intl.formatMessage(
      { id: ATTACH_MODAL_MESSAGE_IDS.poolCaption },
      { count: filteredItems.length, selected: pendingAddCount }
    );
  }, [filteredItems.length, hasNoItems, intl, pendingAddCount]);

  const confirmLabel = useMemo(() => {
    if (pendingAddCount > 0) {
      return intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.confirmCount }, { count: pendingAddCount });
    }

    return intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.confirm });
  }, [intl, pendingAddCount]);

  const confirmDisabled = disabled || hasNoItems || pendingAddCount === 0;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      data-testid={`${testIdPrefix}-modal`}
      PaperProps={{ sx: styles.paper }}
    >
      <DialogTitle sx={styles.titleRoot} component="div">
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={styles.titleStack}>
          <AutoAwesome sx={styles.titleIcon} aria-hidden />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={styles.titleText}>
              {intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.title })}
            </Typography>
            <Typography variant="caption" sx={styles.titleSubtitle}>
              {intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.subtitle })}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={resetAndClose}
          aria-label={intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.closeAria })}
          sx={styles.closeButton}
          data-testid={`${testIdPrefix}-close`}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {hasNoItems ? (
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.empty })}
          </Typography>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.searchPlaceholder })}
              disabled={disabled}
              data-testid={`${testIdPrefix}-search`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={styles.searchField}
            />

            {poolCaption ? (
              <Typography variant="caption" sx={styles.poolCaption}>
                {poolCaption}
              </Typography>
            ) : null}

            {!hasNoSearchResults ? (
              <List dense disablePadding sx={styles.list}>
                {filteredItems.map(item => {
                  const key = getItemKey(item);

                  if (!key) {
                    return null;
                  }

                  const alreadyAttached = attachedKeySet.has(key);
                  const rowSelected = addOnlySelectedKeys.has(key);
                  const labels = resolveContextItemRowLabels(item);

                  return (
                    <ListItemButton
                      key={key}
                      selected={rowSelected}
                      disabled={disabled || alreadyAttached}
                      onClick={() => toggleAddOnlyItem(key)}
                      sx={styles.listItem}
                      data-testid={`${testIdPrefix}-row-${key}`}
                    >
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={alreadyAttached || rowSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                      />
                      <ListItemText
                        primary={labels.primary}
                        secondary={labels.secondary || null}
                        primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: '0.74rem', color: 'text.secondary' }}
                      />
                      {alreadyAttached ? (
                        <Typography variant="caption" sx={styles.attachedBadge}>
                          {intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.attachedBadge })}
                        </Typography>
                      ) : null}
                    </ListItemButton>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2">{intl.formatMessage({ id: ATTACH_MODAL_MESSAGE_IDS.noResults })}</Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button
          type="button"
          onClick={resetAndClose}
          disabled={disabled}
          sx={styles.cancelButton}
          data-testid={`${testIdPrefix}-cancel`}
        >
          {intl.formatMessage({ id: 'cancel' })}
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          sx={styles.confirmButton}
          data-testid={`${testIdPrefix}-done`}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommandIntelligenceAttachModal;
